import type { ReactNode } from "react";
import "../stile/telefon/positionen.css";
import { betragFehler } from "../daten/zahlfeld";
import { Betragssatz } from "./Betragssatz";

/* ══════════════════════════════════════════════════════════════════
   Was an einer Position steht — als Blatt zum Ändern (Telefon).

   Luca, 17.09.2026, nach dem Durchklicken: *„Dann muss das aber halt
   auch voll die Informationen bieten."* Und am 20.09.2026: *„Die Position
   kann ich dann nicht wirklich bearbeiten, nur wenn ich jetzt zum Beispiel
   in der Merkliste den Rüttler klicke und dann auf Details klicke. … Vor
   allem die Details sind auch noch nicht gut formatiert."*

   ── Die Formen des Blatts am Telefon ─────────────────────────────

   **Als Text** standen hier bis zum 08.10.2026 die Angaben unter dem Namen
   (`Postendetails`, `wannText`, `satzText`). Seitdem trägt die aufgeklappte
   Zeile eine Tafel (`Postenaufstellung.tsx`), und die Textfassung ist weg.

   **Als Blatt** (die Formen ab „Das Blatt einer Position am Telefon"):
   Die Sammlung beim Vermieten (`Postenblatt.tsx`) und das Angebot
   (`Positionen.tsx`, `Positionsblatt`) öffnen am Telefon dasselbe Blatt —
   ein Abschnitt je Sache, Zeilen von 56 px, die Zahl rechts, die Einheit
   leise daneben.

   **Was die Rechnung angeht, ändert sich hier nichts.** Die Formen tragen
   Werte und rufen zurück; gerechnet wird, wo es vorher gerechnet wurde
   (`daten/merkliste.ts`, `usePreis`, die API). Die Referenz in
   `docs/43-rechenreferenz.md` bleibt, wie sie ist.

   Klassen: `posb-*` (`stile/telefon/positionen.css`), gegen alle Stile geprüft.
   ══════════════════════════════════════════════════════════════════ */

/* ══════════════════════════════════════════════════════════════════
   Das Blatt einer Position am Telefon — die Formen (20.09.2026).

   Luca: *„Wir hatten eigentlich gesagt, dass du das auch so einbaust, so
   wie beim Direktvermieten, sodass man da dann halt entsprechend auch die
   Position bearbeiten kann."* — und über die Details: *„Da müssen wir das
   auch noch mal komplett überarbeiten."*

   ── Was ein Blatt am Telefon ist ───────────────────────────────────

   Ein Abschnitt je Sache (`Abschnitt` aus `Handyformen.tsx`), darin **eine**
   Karte (`Zeilenblatt`) mit Zeilen von 56 px. Die Formen hier sind die
   Zeilen, die ein Blatt zum **Ändern** braucht und die den Handyformen
   fehlen — dort steht am `Zweizeiler` ausdrücklich: *„Nicht nehmen für ein
   Wertepaar"*, und die `Wertzeile` ist zum Lesen. Wer eine Form verbietet,
   schuldet die Alternative.

   | Form | wofür | Beispiel |
   |---|---|---|
   | `Zahlzeile` | eine Zahl, die man tippt — **rechts, in Mono, die Einheit leise daneben** | „Satz · 55,00 · € je Tag" |
   | `Stueckzeile` | eine Anzahl, die man zählt — zwei Knöpfe, nie eine Tastatur | „Stück · − 1 +" |
   | `Wahlzeile` | eine Wahl aus wenigen — der Haken rechts | „in Summe ✓ · optional" |
   | `Hakenzeile` | ein Ja oder Nein mit Betrag — der Haken links, wie in der Ortsbegehung | „Rücknahme-Check · 15,00" |
   | `Blattsatz` | ein leiser Satz unter einer Karte | „Dieses Gerät geht nur mit Bediener hinaus." |

   **Nicht in ein gestrecktes Textfeld.** Ein Feld über die ganze Breite ist
   für einen Satz da (die Bezeichnung, eine Notiz); eine Zahl darin steht
   allein am linken Rand, und die Einheit fehlt oder hängt irgendwo. Die
   Zahlzeile setzt die Zahl dorthin, wo die Wertzeile sie zum Lesen setzt —
   rechts, `tabular-nums` —, und beim Vergleichen von Satz und Summe stehen
   die Stellen untereinander.

   **Kein Fragezeichen im Blatt.** Am Schreibtisch trägt jede Beschriftung
   ihre Erklärung hinter einem `?`; am Telefon standen davon fünf auf einem
   Schirm, und das ist die Wand (28-designkonzept.md, „Das Fragezeichen").
   Was zu sagen ist, steht als `Blattsatz` unter der Karte — einmal, wo es
   gebraucht wird.

   **Der Zustand ist ein Haken, keine Fläche.** Bis hierher stand „ohne
   Bediener" als gelb gefüllter Umschalter im Blatt; Hausgelb als Fläche ist
   die Hauptaktion, und die gibt es je Maske einmal (28-designkonzept.md,
   „Aktion ist Fläche, Zustand ist Punkt"). Die `Wahlzeile` trägt ihren
   Zustand in `aria-pressed` und zeichnet den Haken in Hausgelb — ein
   Zeichen, keine Fläche.
   ══════════════════════════════════════════════════════════════════ */

/** Ein leiser Satz unter einer Karte — was zu sagen ist, einmal. */
export function Blattsatz({ art, children }: { art?: "acht" | "stopp" | undefined; children: ReactNode }) {
  return <p className="posb-satz" {...(art ? { "data-art": art } : {})}>{children}</p>;
}

/**
 * Eine Zahl, die man tippt: Beschriftung links, die Zahl rechts in Mono,
 * die Einheit leise daneben. Geschrieben wird beim Verlassen (`fertig`);
 * `aendern` hält den Text, damit „18," nicht zu „18,00" wird, bevor man
 * fertig ist.
 */
export function Zahlzeile({ was, dazu, wert, platz, einheit, aendern, fertig, gesperrt, grund, schild, betrag = false, beimAntippen }: {
  was: string;
  /** Die leise Zeile unter der Beschriftung — „× 5 Tage = 275,00", „von Hand statt 55,00". */
  dazu?: ReactNode;
  wert: string;
  /** Der Platzhalter im leeren Feld — der gerechnete Satz. */
  platz?: string | undefined;
  einheit?: string | undefined;
  aendern: (text: string) => void;
  /** Beim Verlassen des Feldes — hier wird geschrieben. */
  fertig?: ((text: string) => void) | undefined;
  gesperrt?: boolean | undefined;
  /** Warum gesperrt — steht als `title` am Feld. */
  grund?: string | undefined;
  /** Das `aria-label`, wo die Beschriftung nicht reicht („Einzel" → „Einzelpreis"). */
  schild?: string | undefined;
  /** Ein Preis: keine dritte Nachkommastelle (E11, Migration 0226) — gesagt, nicht verschluckt (N4-06). */
  betrag?: boolean | undefined;
  /** Beim Antippen des Feldes — etwa den Vorschlag im Platzhalter übernehmen (Mengenfeld, 08.10.2026). */
  beimAntippen?: (() => void) | undefined;
}) {
  /* Ein Betrag mit dritter Stelle oder ohne Zahl schickt nichts (N4-06):
     Das Feld behält den Text, wird rot, und der Satz darunter sagt warum —
     bis dahin schnitt es die dritte Ziffer still ab. */
  const fehler = betrag ? betragFehler(wert) : null;
  return (
    <>
      <label className="posb-zahlzeile">
        <span className="posb-zeile-was">
          <b>{was}</b>
          {dazu ? <small>{dazu}</small> : null}
        </span>
        <input
          className="posb-zahlfeld"
          inputMode="decimal"
          autoComplete="off"
          enterKeyHint="done"
          aria-label={schild ?? was}
          aria-invalid={fehler !== null}
          value={wert}
          placeholder={platz}
          disabled={gesperrt}
          title={grund}
          onChange={(e) => aendern(e.target.value)}
          onClick={beimAntippen}
          onBlur={(e) => { if (!betrag || betragFehler(e.target.value) === null) fertig?.(e.target.value); }}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        />
        {einheit ? <span className="posb-zeile-einheit">{einheit}</span> : null}
      </label>
      {betrag ? <Betragssatz text={wert} /> : null}
    </>
  );
}

/**
 * Eine Anzahl, die man zählt — zwei Knöpfe und die Zahl dazwischen. Am
 * Telefon ist „noch einen zweiten" ein Tipp und kein Zahlenfeld.
 */
export function Stueckzeile({ was, dazu, wert, min = 1, max, weniger, mehr, wenigerSchild, mehrSchild }: {
  was: string;
  dazu?: ReactNode;
  wert: number;
  min?: number;
  max: number;
  weniger: () => void;
  mehr: () => void;
  wenigerSchild: string;
  mehrSchild: string;
}) {
  return (
    <div className="posb-stueckzeile">
      <span className="posb-zeile-was">
        <b>{was}</b>
        {dazu ? <small>{dazu}</small> : null}
      </span>
      <span className="posb-schritte">
        <button type="button" className="posb-schritt" aria-label={wenigerSchild} disabled={wert <= min} onClick={weniger}>−</button>
        <b className="posb-schritt-zahl">{wert}</b>
        <button type="button" className="posb-schritt" aria-label={mehrSchild} disabled={wert >= max} onClick={mehr}>+</button>
      </span>
    </div>
  );
}

/** Eine Zeile, die eine Wahl trägt — der Haken steht rechts, wie im Notizblatt. */
export function Wahlzeile({ wort, unter, rechts, an, waehlen, gesperrt, grund }: {
  wort: ReactNode;
  /** Die leise Zeile darunter — warum, wer, bis wann. */
  unter?: ReactNode;
  /** Rechts vor dem Haken: eine Kennung, eine Zahl. */
  rechts?: ReactNode;
  an: boolean;
  waehlen: () => void;
  gesperrt?: boolean | undefined;
  grund?: string | undefined;
}) {
  return (
    <button
      type="button"
      className="posb-wahl"
      aria-pressed={an}
      disabled={gesperrt}
      title={grund}
      onClick={waehlen}
    >
      <span className="posb-zeile-text">
        <b>{wort}</b>
        {unter ? <small>{unter}</small> : null}
      </span>
      {rechts ? <span className="posb-zeile-rechts">{rechts}</span> : null}
      {an ? <span className="posb-wahl-haken" aria-hidden="true" /> : null}
    </button>
  );
}

/**
 * Ein Ja oder Nein mit Betrag — der Haken links, gezeichnet wie in der
 * Ortsbegehung, der Betrag rechts in Mono. Angewählt ist der Regelfall; also
 * trägt nicht die Wahl eine Fläche, sondern die Abwahl (`aus`): Name und
 * Betrag treten zurück, der Betrag ist durchgestrichen. Ein Zuschlag, der
 * spurlos verschwindet, ist einer, über den drei Wochen später niemand mehr
 * reden kann.
 */
export function Hakenzeile({ wort, unter, rechts, an, aendern, aus, pruefen, gesperrt, grund }: {
  wort: ReactNode;
  unter?: ReactNode;
  rechts?: ReactNode;
  an: boolean;
  aendern: (an: boolean) => void;
  /** Abgewählt — die Zeile bleibt stehen und sagt, dass sie entfällt. */
  aus?: boolean | undefined;
  /** Ein Betrag, den niemand entschieden hat — die zweite Zeile in Achtungsfarbe. */
  pruefen?: boolean | undefined;
  gesperrt?: boolean | undefined;
  grund?: string | undefined;
}) {
  return (
    <label
      className="posb-haken"
      data-aus={aus ? "ja" : undefined}
      data-pruefen={pruefen ? "ja" : undefined}
      title={grund}
    >
      <input type="checkbox" checked={an} disabled={gesperrt} onChange={(e) => aendern(e.target.checked)} />
      <span className="posb-zeile-text">
        <b>{wort}</b>
        {unter ? <small>{unter}</small> : null}
      </span>
      {rechts ? <span className="posb-zeile-rechts">{rechts}</span> : null}
    </label>
  );
}
